import React from 'react';
import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const numberFormatter = new Intl.NumberFormat('ro-RO', {
  maximumFractionDigits: 0,
});

function formatValue(value, suffix) {
  if (value === null || typeof value === 'undefined') return '—';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  return `${numberFormatter.format(numericValue)}${suffix || ''}`;
}

function ClientMetricCard({
  title,
  value,
  suffix,
  detail,
  comparison,
  comparisonLabel = 'vs previous period',
  comparisonSuffix = '%',
  definition,
  accent = '#1a237e',
  loading = false,
}) {
  const hasValue = value !== null && typeof value !== 'undefined';
  const hasComparison = comparison !== null && typeof comparison !== 'undefined';
  const comparisonNumber = Number(comparison || 0);
  const positive = comparisonNumber >= 0;

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        minHeight: { xs: 150, sm: 156 },
        borderRadius: { xs: '14px', sm: '16px' },
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
        border: '1px solid rgba(15, 23, 42, 0.06)',
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: accent,
        },
      }}
    >
      {loading && hasValue && (
        <LinearProgress
          aria-label={`Refreshing ${title}`}
          sx={{ position: 'absolute', top: 4, left: 0, right: 0, height: 2 }}
        />
      )}
      <CardContent
        sx={{
          height: '100%',
          boxSizing: 'border-box',
          p: { xs: 1.5, sm: 1.75, md: 2 },
          '&:last-child': { pb: { xs: 1.5, sm: 1.75, md: 2 } },
        }}
      >
        <Stack height="100%" justifyContent="space-between" spacing={1.2}>
          <Box minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                sx={{
                  color: accent,
                  fontWeight: 800,
                  letterSpacing: '.015em',
                  fontSize: { xs: '0.72rem', sm: '0.8rem', md: '0.84rem' },
                  textTransform: 'uppercase',
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              {definition && (
                <Tooltip title={definition} arrow>
                  <InfoOutlinedIcon
                    aria-label={`${title} definition`}
                    tabIndex={0}
                    sx={{ color: '#64748b', fontSize: 16 }}
                  />
                </Tooltip>
              )}
            </Stack>

            {loading && !hasValue ? (
              <Skeleton width="58%" height={54} />
            ) : (
              <Typography
                sx={{
                  mt: 0.65,
                  color: '#111827',
                  fontWeight: 800,
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: { xs: '1.85rem', sm: '2.15rem', md: '2.3rem' },
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {formatValue(value, suffix)}
              </Typography>
            )}
          </Box>

          <Stack spacing={0.45} minWidth={0}>
            {(hasComparison || comparison === null) && (
              <Stack direction="row" alignItems="center" spacing={0.7} minWidth={0}>
                {hasComparison ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.25,
                      px: 0.65,
                      py: 0.25,
                      borderRadius: 999,
                      bgcolor: positive
                        ? 'rgba(46, 125, 50, 0.10)'
                        : 'rgba(211, 47, 47, 0.10)',
                      flexShrink: 0,
                    }}
                  >
                    <SvgIcon
                      color={positive ? 'success' : 'error'}
                      sx={{ fontSize: 16 }}
                    >
                      {positive ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                    </SvgIcon>
                    <Typography
                      color={positive ? 'success.main' : 'error.main'}
                      sx={{ fontWeight: 800, fontSize: '0.8rem', lineHeight: 1.2 }}
                    >
                      {Math.abs(comparisonNumber).toFixed(1)}{comparisonSuffix}
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                    No previous baseline
                  </Typography>
                )}
                {hasComparison && (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: { xs: '0.7rem', sm: '0.76rem' },
                      lineHeight: 1.2,
                      minWidth: 0,
                    }}
                  >
                    {comparisonLabel}
                  </Typography>
                )}
              </Stack>
            )}
            {detail && (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  lineHeight: 1.25,
                }}
              >
                {detail}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default ClientMetricCard;
